import { FileInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import PropTypes from 'prop-types';
import { formatFileName } from 'utils/func';
import { uploadSignature } from 'apis/contacts.api';
import { toast } from 'layout';
import { TOAST } from 'constant';

export default function SignatureUpload({
  contactId,
  fileUrl,
  visible,
  setVisible,
  refetch,
  refetchPreview,
  handleDownloadFile,
}) {
  // #region Data
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, dirtyFields },
  } = useForm();

  const { mutateAsync, isLoading } = useMutation(uploadSignature);
  // #endregion Data

  // #region Event

  const onSubmit = async (data) => {
    if (data.file) {
      const formData = new FormData();
      formData.append('file', data.file);
      try {
        await mutateAsync({ id: contactId, file: formData });
        toast(TOAST.SUCCESS, t('userProfile.uploadSignatureSuccess'), 2000);
        refetch();
        if (refetchPreview) {
          refetchPreview();
        }
        reset();
        setVisible(false);
      } catch (error) {
        toast(TOAST.SUCCESS, error.response.data.message);
      }
    }
  };

  // #endregion Event

  return (
    <Dialog
      header={t('userProfile.uploadSignature')}
      visible={visible}
      style={{ maxWidth: '700px' }}
      draggable={false}
      onHide={() => {
        reset();
        setVisible(false);
      }}
    >
      <div className="relative">
        <form className="p-fluid">
          <FileInput
            name="file"
            accept=".jpg,.png"
            control={control}
            errors={errors}
            acceptOnly="imageOnly"
          />
          {fileUrl && (
            <span
              className="flex align-items-center p-button p-button-text p-button-sm"
              onClick={handleDownloadFile}
            >
              <i className="pi pi-image text-orange-400 text-xl" />
              <span className="ml-1 text-orange-400 underline font-italic text-base">
                {formatFileName(fileUrl)}
              </span>
            </span>
          )}

          <h5 className="m-0 mb-1">Kích thước ảnh chữ ký phù hợp:</h5>
          <ul className="list-none m-0 p-0">
            <li className="flex align-items-center py-1">
              <i className="pi pi-check text-green-500 mr-2" />
              <span className="text-base">
                Kích thước pixel: Tối thiểu 600 x 200 px (chất lượng cao nếu chữ ký có nhiều nét)
              </span>
            </li>
            <li className="flex align-items-center py-1">
              <i className="pi pi-check text-green-500 mr-2" />
              <span className="text-base">Tỉ lệ khung hình: khoảng 3:1 đến 4:1</span>
            </li>
            <li className="flex align-items-center py-1">
              <i className="pi pi-check text-green-500 mr-2" />
              <span className="text-base">Định dạng file: .png (có nền trong suốt) hoặc .jpg</span>
            </li>
            <li className="flex align-items-center py-1">
              <i className="pi pi-check text-green-500 mr-2" />
              <span className="text-base">Dung lượng: Dưới 500 KB</span>
            </li>
          </ul>

          <div className="flex flex-column sm:flex-row align-items-center sm:column-gap-3 justify-items-center">
            <Button
              label={t('formLayout.action.cancel')}
              type="button"
              className="mt-4 w-full sm:w-21rem"
              severity="danger"
              onClick={() => {
                reset();
                setVisible(false);
              }}
            />
            <Button
              label={t('formLayout.save')}
              className="mt-2 sm:mt-4 w-full sm:w-21rem"
              severity="info"
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!dirtyFields?.file}
            />
          </div>
        </form>
        {isLoading && <Loading />}
      </div>
    </Dialog>
  );
}

SignatureUpload.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  contactId: PropTypes.string,
  fileUrl: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  refetchPreview: PropTypes.func,
  handleDownloadFile: PropTypes.func.isRequired,
};

SignatureUpload.defaultProps = {
  fileUrl: '',
  contactId: '',
};
